import output from "_src/engine/output";
import {easingType, geometry} from "_src/engine/constants";
import intersectEvaluator from "_src/engine/intersect_evaluator";

import {vec3, vec4, quat} from "_src/libs/gl-matrix";
import utils from "_src/client/controls/utils";
import TargetControl from "_src/client/controls/target_control";

const _vec3Tmp = vec3.create();
const _vec3Tmp2 = vec3.create();

const _quatTmp = quat.create();
const _quatTmp2 = quat.create();

export default class OrbitControl extends TargetControl {

  constructor(object) {

    super(object);

    this.limitVertical = false;
    this.horRotationSign = 1;

  }

  rotate(hor, vert) {

    if (!this.enabled) {
      return;
    }

    const object = this.object;

    const worldX = vec3.transformQuat(_vec3Tmp, geometry.AXIS_X, object.quat);

    const horRotation = quat.setAxisAngle(_quatTmp, geometry.AXIS_Z, -hor * this.upsideDownSign);
    const vertRotation = quat.setAxisAngle(_quatTmp2, worldX, vert);

    const deltaRotation = quat.multiply(_quatTmp, horRotation, vertRotation);
    const newRotation = quat.multiply(_quatTmp2, deltaRotation, object.quat);

    const direction = vec3.set(_vec3Tmp, 0, 0, -1);
    vec3.transformQuat(direction, direction, newRotation);

    const [horAngle, vertAngle] = utils.anglesFromView(direction);

    if (Math.abs(vec3.dot(direction, geometry.AXIS_Z)) < 0.9999999) {
      this.isUpsideDown = utils.checkIsUpsideDown(newRotation, this.upVec);
      this.horAngle = horAngle;
    }

    this.vertAngle = vertAngle;

    this.updatePosition();
    this.updateRotation();

  }

  pan(dX, dY) {
    if (!this.enabled) {
      return;
    }

    const object = this.object;

    let objX;
    if (this.isUpsideDown) {
      objX = vec3.cross(_vec3Tmp, geometry.AXIS_MZ, this.direction);
    } else {
      objX = vec3.cross(_vec3Tmp, geometry.AXIS_Z, this.direction);
    }

    vec3.normalize(objX, objX);

    const objY = vec3.cross(_vec3Tmp2, this.direction, objX);

    vec3.scale(objX, objX, dX * this.dist * 0.05);
    vec3.scale(objY, objY, dY * this.dist * 0.05);

    vec3.add(object.trans, object.trans, objX);
    vec3.add(object.trans, object.trans, objY);

    vec3.add(this.target, this.target, objX);
    vec3.add(this.target, this.target, objY);

    this.updatePosition();
  }

  _zoomCameraToCursor(delta, cameraX, cameraY) {

    const object = this.object;

    const dist = calcDistantToTargetPlane(object, delta, cameraX, cameraY, this.target, this.direction);
    if (dist < 0) {
      output.error(output.DEVELOPERS_ERROR, "can't zoom to cursor");
      return;
    }

    if (object.isPerspective()) {

      const step = dist * (-delta);

      if (step > 0 && step < this.minDist) {
        const rayToCursor = object._calculateRayPersp(cameraX, cameraY, _vec3Tmp);
        vec3.scaleAndAdd(this.target, this.target, rayToCursor, this.minDist);
        this.dist = Math.max(this.minDist, this.dist);
        return;
      }

    }

    this.dist = vec3.distance(object.trans, this.target);
  }

  setTarget(target, onUpdate, onFinish) {
    if (!this.enabled) {
      return;
    }

    const startTarget = vec3.clone(this.target);
    const finalTarget = target;
    const startPosition = vec3.clone(this.object.trans);
    const startRotation = quat.clone(this.object.quat);

    vec3.copy(this.target, target);
    this.setPosition(this.object.trans);
    this.updatePosition();
    this.updateRotation();

    const finalPosition = vec3.clone(this.object.trans);
    const finalRotation = quat.clone(this.object.quat);

    this._animation.setOnUpdate((value) => {
      vec3.lerp(this.target, startTarget, finalTarget, value);
      quat.slerp(this.object.quat, startRotation, finalRotation, value);
      vec3.lerp(this.object.trans, startPosition, finalPosition, value);
      this.object.copyRotation(this.object.quat);
      this.setPosition(this.object.trans);

      onUpdate();
    });

    this._animation.setOnFinish(() => {
      vec3.copy(this.target, finalTarget);
      this.object.copyRotation(finalRotation);
      this.setPosition(finalPosition);

      onFinish();
    });

    this._animation.type = easingType.IN_OUT_SINE;

    this._animation.start(0, 1, 0.2);
  }

  moveToMinmax(minmax, rect, animation, onStart, onUpdate, onFinish, time = 0.2) {
    super.moveToMinmax(minmax, rect, animation, onStart, onUpdate, onFinish, time, null);
  }

}

const calcDistantToTargetPlane = (function() {

  const _vec3Tmp = vec3.create();
  const _vec3Tmp2 = vec3.create();
  const _vec3Tmp3 = vec3.create();
  const _vec3Tmp4 = vec3.create();
  const _vec3Tmp5 = vec3.create();

  const _vec4Tmp = vec4.create();

  return function(camera, delta, cameraX, cameraY, target, direction) {

    const dist = vec3.distance(camera.trans, target);
    const step = 0.5 * dist * (-delta);

    const rayToCursor = _vec3Tmp2;
    if (camera.isPerspective()) {
      camera._calculateRayPersp(cameraX, cameraY, rayToCursor);
    } else if (camera.isOrtho()) {
      const dw = 0.5 * cameraX * camera.right * Math.abs(delta);
      const dh = 0.5 * cameraY * camera.top * Math.abs(delta);

      vec3.set(rayToCursor, dw, dh, -Math.abs(step));
      vec3.transformQuat(rayToCursor, rayToCursor, camera.quat);
      vec3.normalize(rayToCursor, rayToCursor);
    } else {
      output.error("Coudn't zoom to cursor: incorrect camera type.");
      return -1;
    }

    const initialOrigin = camera._calculateOriginPersp(_vec3Tmp);
    const targetPlane = vec3.copy(_vec4Tmp, direction);
    targetPlane[3] = -vec3.dot(targetPlane, target);

    const t = intersectEvaluator.rayPlaneIntersection(targetPlane, camera.trans, rayToCursor, camera.trans);
    if (t < 0) {
      return -1;
    }

    vec3.scaleAndAdd(camera.trans, initialOrigin, rayToCursor, step);
    camera.updateMatrix();

    const zoomedOrigin = camera._calculateOriginPersp(_vec3Tmp3);
    const targetScreen = camera.projectPoint(target[0], target[1], target[2], _vec3Tmp5);
    const rayToNewTarget = camera._calculateRayPersp(targetScreen[0], targetScreen[1], _vec3Tmp4);

    const t2 = intersectEvaluator.rayPlaneIntersection(targetPlane, zoomedOrigin, rayToNewTarget, target);
    if (t2 < 0) {
      return -1;
    }

    return t;

  }

})();
